import { MAP_FOLLOW, navigationZoom } from '@konvoi/shared';

import type { FollowMode } from '../map/ConvoyMap.js';

/**
 * Wem die Karte gerade folgt.
 *
 * Vier Fälle, und die Reihenfolge ist die Aussage:
 *
 * 1. **Der Fahrer hat die Karte selbst verschoben** — dann gehört sie ihm, auch
 *    während einer Navigation. Sie ihm wegzuziehen wäre übergriffig. Zurück
 *    kommt er über das Fadenkreuz.
 * 2. **Ein kurzer Blick auf die Führung läuft.** Während einer Navigation ist
 *    „Führungsfahrzeug zeigen" kein Zustand, sondern ein Blick — sehen und
 *    weiterfahren, ohne noch eine Fläche zu treffen.
 * 3. **Eine Navigation läuft** — dann die Navigationsansicht: das eigene
 *    Fahrzeug unten, der Platz darüber für den Weg.
 * 4. **Sonst die Vorgabe der Rolle.** Wer führt, will die ganze Kolonne sehen;
 *    wer hinterherfährt, sich selbst.
 *
 * `folgen` bleibt vom Blick und von der Navigation unberührt: Endet die
 * Navigation, steht dort noch die Wahl, die der Fahrer vorher getroffen hat —
 * und genau dorthin kehrt die Karte zurück.
 *
 * Steht hier und nicht in der Komponente, damit die Rangfolge prüfbar ist. Am
 * 28.08.2026 auf der Ausfahrt „Henglarn" blieb ein Blick hängen, und weil er
 * über allem außer `free` steht, wirkte danach auch das Fadenkreuz nicht mehr
 * — siehe A-79.
 */
export function kartenführung(lage: {
  /** Die zuletzt getroffene Wahl, oder `null`, solange keine getroffen wurde. */
  readonly folgen: FollowMode | null;
  /** Läuft gerade ein kurzer Blick auf die Führung? */
  readonly blick: boolean;
  readonly navigationLäuft: boolean;
  readonly binFührung: boolean;
}): FollowMode {
  if (lage.folgen === 'free') {
    return 'free';
  }
  if (lage.blick) {
    return 'leader';
  }
  if (lage.navigationLäuft) {
    return 'navigation';
  }
  return lage.folgen ?? (lage.binFührung ? 'all' : 'self');
}

/**
 * Welche Vergrößerung eine Ansicht setzt — oder keine.
 *
 * ## Warum „mein Fahrzeug" eine braucht
 *
 * Bis zum 02.09.2026 setzte nur die Navigationsansicht eine Vergrößerung.
 * `self` ließ sie stehen, wie sie war, und erbte damit die der vorigen Ansicht.
 * Das Einpassen der Kolonne rechnet sie aber aus den Grenzen: Bei einer
 * auseinandergezogenen Kolonne kommt dabei ohne Weiteres Zoom 11 heraus — auf
 * einem iPhone rund **17 800 Meter Breite**. Wer danach das Fadenkreuz tippte,
 * sah sich selbst als Punkt in einem Ausschnitt, in dem die eigene Straße nicht
 * zu erkennen ist.
 *
 * ## Warum dieselbe Funktion wie die Navigation
 *
 * Die Frage ist in beiden Ansichten dieselbe: Wie viel Straße brauche ich bei
 * diesem Tempo im Bild? Sie hängt am Tempo und nicht daran, ob eine Navigation
 * läuft. Eine zweite Stützstellenpaarung wäre nach Abschnitt 7 des Auftrags
 * eine Zahl zu viel — und nach der Begründung von A-35: „Zwei Zahlen für
 * dieselbe physikalische Tatsache wären eine zu viel; auseinandernehmen lassen
 * sie sich, wenn die Praxis es verlangt."
 *
 * ## Was das kostet, ausdrücklich benannt
 *
 * Die Vergrößerung von Hand zu ändern und in `self` zu behalten, geht nicht
 * mehr: Zwei Finger auf der Karte setzen ohnehin `free`, und von dort holte das
 * Fadenkreuz den selbst eingestellten Wert bisher mit herüber. Künftig
 * überschreibt ihn das Tempo. Zwei Finger sind nach Abschnitt 2 keine
 * Fahrbedienung, und für den Stand bleibt `free`: Dort liegt die Karte genau
 * so, wie der Fahrer sie hingelegt hat. **`free` ist damit die Ansicht von
 * Hand, `self` die selbsttätige.**
 *
 * `null` heißt: Diese Ansicht setzt keine. `all` und `leader` rechnen sie beim
 * Einpassen aus den Grenzen — gäbe diese Stelle dort auch eine an,
 * überschrieben sich beide. Und `free` gehört dem Fahrer.
 */
export function kartenZoom(
  modus: FollowMode,
  speedMps: number | null,
  letzter: number | null,
): number | null {
  if (modus !== 'self' && modus !== 'navigation') {
    return null;
  }
  return navigationZoom(speedMps, letzter);
}

/**
 * Wie weit sich ein Wert in diesem Bild seinem Ziel nähert.
 *
 * Über die **Zeit** und nicht über die Bildrate — dieselbe Zeitkonstante und
 * dieselbe Begründung wie bei den Fahrzeugsymbolen: Nach dem Aufwachen aus dem
 * Hintergrund drosselt iOS die Bildrate, und ein fester Anteil je Bild ließe
 * dieselbe Bewegung ein Vielfaches länger dauern.
 *
 * Ein unsinniger Abstand — eine rückwärts laufende Uhr, ein `NaN` — ergibt
 * null: lieber stehen bleiben als springen.
 */
export function annaeherungsanteil(abstandMs: number): number {
  if (!Number.isFinite(abstandMs) || abstandMs <= 0) {
    return 0;
  }
  return 1 - Math.exp(-abstandMs / MAP_FOLLOW.approachTauMs);
}

/**
 * Nähert einen Winkel seinem Ziel auf dem **kürzeren** Weg.
 *
 * Von 350° auf 10° sind es zwanzig Grad nach rechts, nicht dreihundertvierzig
 * nach links. Ohne diese Unterscheidung drehte sich die Karte bei jedem
 * Nulldurchgang einmal fast ganz herum.
 */
export function winkelAnnaeherung(ist: number, ziel: number, anteil: number): number {
  const abstand = (((ziel - ist) % 360) + 540) % 360 - 180;
  return ((ist + abstand * anteil) % 360 + 360) % 360;
}

/** Der Stand der Kamera zwischen zwei Bildern. */
export interface Kamerastand {
  readonly zoom: number | null;
  readonly bearing: number | null;
}

/**
 * Ein Bild der Kameraführung: Vergrößerung und Ausrichtung dem Ziel nähern.
 *
 * ## Warum die Karte das nicht selbst macht
 *
 * Weil sie es nicht kann, solange wir sie in jedem Bild neu anweisen.
 * `Camera.easeTo` beginnt mit `_stop()` — die laufende Kamerafahrt bricht ab,
 * die neue beginnt von vorn und kommt nie an. Am 03.09.2026 hinkte die Karte
 * dadurch bis zu 290 Meter hinterher und sprang dann, sobald `jumpDistanceM`
 * überschritten war (A-94).
 *
 * Geglättet wird deshalb **hier**, Bild für Bild, und die Karte bekommt das
 * Ergebnis als Sprung. Sie führt nichts mehr selbst aus.
 *
 * ## Das Einrasten
 *
 * Eine geometrische Annäherung erreicht ihr Ziel nie ganz. Ohne den Fangwert
 * änderten sich Vergrößerung und Ausrichtung noch nach Minuten um unsichtbare
 * Bruchteile, und MapLibre meldete für jede dieser Änderungen Zoom- und
 * Drehereignisse. Unter einem Tausendstel Zoomstufe und einem Hundertstel Grad
 * ist nichts mehr zu sehen.
 *
 * Ohne bisherigen Stand — beim ersten Bild — wird das Ziel unmittelbar
 * übernommen. Sonst kröche die Karte beim Öffnen aus einer beliebigen Lage
 * heran.
 */
export function kameraSchritt(
  ist: Kamerastand,
  ziel: { readonly zoom: number | null; readonly bearing: number },
  abstandMs: number,
): { zoom: number | null; bearing: number } {
  const anteil = annaeherungsanteil(abstandMs);

  const einrasten = (jetzt: number, hin: number, genauigkeit: number): number =>
    Math.abs(hin - jetzt) < genauigkeit ? hin : jetzt;

  const bearing =
    ist.bearing === null
      ? ziel.bearing
      : einrasten(winkelAnnaeherung(ist.bearing, ziel.bearing, anteil), ziel.bearing, 0.01);

  if (ziel.zoom === null) {
    return { zoom: ist.zoom, bearing };
  }

  const zoom =
    ist.zoom === null
      ? ziel.zoom
      : einrasten(ist.zoom + (ziel.zoom - ist.zoom) * anteil, ziel.zoom, 0.001);

  return { zoom, bearing };
}
